import { EyeOff, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { useUpdateReview } from '@/features/review/hooks/useReview';

// =========================================
// Props
// =========================================

interface BlindToggleProps {
  reviewId: string;
  blinded: boolean;
  disabled?: boolean;
}

// =========================================
// BlindToggle Component
// =========================================

export function BlindToggle({
  reviewId,
  blinded,
  disabled = false,
}: BlindToggleProps) {
  const updateReview = useUpdateReview();

  const handleToggle = async (checked: boolean) => {
    try {
      await updateReview.mutateAsync({
        reviewId,
        blinded: checked,
      });
      toast.success(
        checked
          ? 'Blinded mode enabled — reviewers cannot see each other\'s decisions'
          : 'Blinded mode disabled — decisions are now visible to all reviewers'
      );
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to update blinding'
      );
    }
  };

  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-lg border p-3',
        blinded
          ? 'bg-indigo-50 border-indigo-200'
          : 'bg-slate-50 border-slate-200'
      )}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-3">
            {blinded ? (
              <EyeOff className="h-4 w-4 text-indigo-600" />
            ) : (
              <Eye className="h-4 w-4 text-slate-500" />
            )}
            <div className="space-y-0.5">
              <Label
                htmlFor="blind-toggle"
                className="text-sm font-medium cursor-pointer"
              >
                Blinded Review
              </Label>
              <p className="text-xs text-slate-500">
                {blinded
                  ? 'Reviewers cannot see others\' decisions'
                  : 'Decisions visible to all reviewers'}
              </p>
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          When enabled, reviewers screen sources independently without seeing
          others&apos; decisions, reducing bias. Disable to allow open
          discussion.
        </TooltipContent>
      </Tooltip>
      <Switch
        id="blind-toggle"
        checked={blinded}
        onCheckedChange={handleToggle}
        disabled={disabled || updateReview.isPending}
        className="ml-auto"
      />
    </div>
  );
}
